/**
 * DataTable — Mobbin/Deel-density generic table (TASK 1-5).
 *
 * Features:
 *   - 44 px row height, 13–14 px text, matching Deel web Jan 2026 63.png.
 *   - Column header click → sort asc/desc/none (tri-state).
 *   - Filter chip strip with per-chip multi-select popovers.
 *   - "Configure columns" toolbar button opens a right-side drawer.
 *   - Pagination (page-size selector + prev/next).
 *   - Accessible: focus ring on rows (`tabIndex={0}` + Enter/Space → onRowClick).
 *   - Loading state: centered spinner in the table body.
 *   - Empty state with custom message.
 *
 * There is NO @tanstack/react-table dependency — everything is handled
 * with memoized native array operations so the component adds zero new
 * npm dependencies.
 *
 * Visual references:
 *   C:/PROJECT_GGJ_B2C/MobbinUI/UIPattern B2C/Table&Filter/Deel web Jan 2026 63.png
 *   C:/PROJECT_GGJ_B2C/MobbinUI/UIPattern B2C/Table&Filter/Deel web Jan 2026 64.png
 */

import { ArrowDown, ArrowUp, ArrowUpDown, Settings2 } from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import LoadingSpinner from '../LoadingSpinner';
import ConfigureColumnsDrawer from './ConfigureColumnsDrawer';
import FilterChips from './FilterChips';
import type { Column, DataTableProps, SortDir } from './types';

const DEFAULT_PAGE_SIZES = [20, 50, 100];

function DataTable<T extends Record<string, unknown>>({
  columns,
  rows,
  filters        = [],
  onFilterChange,
  onRowClick,
  headerLeft,
  actions,
  pageSizeOptions   = DEFAULT_PAGE_SIZES,
  initialPageSize,
  onColumnsChange,
  emptyMessage      = 'No results found.',
  loading           = false,
  onPagedRowsChange,
  serverPagination  = false,
  totalRows,
  page: pageProp,
  onPageChange,
  onPageSizeChange,
  ariaLabel         = 'Data table',
  className         = '',
  reserveFloatingSpace = false,
}: DataTableProps<T>) {
  // ── sort state ──────────────────────────────────────────────────────────
  const [sortKey, setSortKey]   = useState<string | null>(null);
  const [sortDir, setSortDir]   = useState<SortDir>(null);

  // ── filter state ────────────────────────────────────────────────────────
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});

  // ── column visibility ───────────────────────────────────────────────────
  const initialVisibility = useMemo<Record<string, boolean>>(() => {
    return Object.fromEntries(columns.map((c) => [c.key, !c.defaultHidden]));
  }, [columns]);
  const [visibility, setVisibility] = useState<Record<string, boolean>>(initialVisibility);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // ── pagination ──────────────────────────────────────────────────────────
  const [pageSize, setPageSize]       = useState<number>(initialPageSize ?? pageSizeOptions[0]);
  const [internalPage, setInternalPage] = useState(1);

  // In server mode the parent owns the current page; otherwise it's internal.
  const page = serverPagination ? Math.max(1, pageProp ?? 1) : internalPage;

  const goToPage = useCallback((next: number) => {
    if (serverPagination) onPageChange?.(next);
    else setInternalPage(next);
  }, [serverPagination, onPageChange]);

  const changePageSize = useCallback((size: number) => {
    setPageSize(size);
    if (serverPagination) { onPageSizeChange?.(size); onPageChange?.(1); }
    else setInternalPage(1);
  }, [serverPagination, onPageSizeChange, onPageChange]);

  // ── derived data ────────────────────────────────────────────────────────

  /** Columns currently shown in the table. */
  const visibleColumns = useMemo(
    () => columns.filter((c) => c.alwaysVisible || visibility[c.key] !== false),
    [columns, visibility],
  );

  /** Rows after client-side filter (if onFilterChange is not provided). */
  const filteredRows = useMemo<T[]>(() => {
    if (onFilterChange || serverPagination) return rows; // server-side filtering

    return rows.filter((row) =>
      Object.entries(filterValues).every(([id, selected]) => {
        if (!selected.length) return true;
        const col = columns.find((c) => c.key === id);
        if (!col) return true;
        const val = String(row[col.key] ?? '').toLowerCase();
        return selected.some((s) => s.toLowerCase() === val);
      }),
    );
  }, [rows, filterValues, columns, onFilterChange]);

  /** Rows after sort. */
  const sortedRows = useMemo<T[]>(() => {
    if (!sortKey || !sortDir) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      const av = a[sortKey as keyof T];
      const bv = b[sortKey as keyof T];

      let cmp = 0;
      if (av == null && bv == null) cmp = 0;
      else if (av == null) cmp = 1;
      else if (bv == null) cmp = -1;
      else if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
      else cmp = String(av).localeCompare(String(bv));

      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filteredRows, sortKey, sortDir]);

  /** Rows for the current page. In server mode `rows` is already the page. */
  const pagedRows = useMemo<T[]>(() => {
    if (serverPagination) return sortedRows;
    const start = (page - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, page, pageSize, serverPagination]);

  const totalCount = serverPagination ? (totalRows ?? sortedRows.length) : sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  useEffect(() => {
    onPagedRowsChange?.(pagedRows);
  }, [pagedRows, onPagedRowsChange]);

  const showLoadingOverlay = loading && rows.length === 0;

  // ── handlers ─────────────────────────────────────────────────────────────

  const handleSort = useCallback((key: string) => {
    setSortKey((prev) => {
      if (prev !== key) { setSortDir('asc'); return key; }
      setSortDir((d) => {
        if (d === 'asc') return 'desc';
        if (d === 'desc') { return null; }
        return 'asc';
      });
      return prev;
    });
    goToPage(1);
  }, [goToPage]);

  const handleFilterChange = useCallback((id: string, values: string[]) => {
    setFilterValues((prev) => {
      const next = { ...prev, [id]: values };
      onFilterChange?.(next);
      return next;
    });
    goToPage(1);
  }, [onFilterChange, goToPage]);

  const handleClearAll = useCallback(() => {
    setFilterValues({});
    onFilterChange?.({});
    goToPage(1);
  }, [onFilterChange, goToPage]);

  const handleSaveColumns = useCallback((vis: Record<string, boolean>) => {
    setVisibility(vis);
    onColumnsChange?.(vis);
  }, [onColumnsChange]);

  const handleRowKeyDown = useCallback((e: React.KeyboardEvent, row: T) => {
    if ((e.key === 'Enter' || e.key === ' ') && onRowClick) {
      e.preventDefault();
      onRowClick(row);
    }
  }, [onRowClick]);

  // ── render ───────────────────────────────────────────────────────────────

  return (
    <div className={`ds-data-table flex flex-col gap-3 w-full ${className}`.trim()}>
      {/* Toolbar — one unified row: [headerLeft] [filters] [actions] [Columns] */}
      {(headerLeft || filters.length > 0 || actions || onColumnsChange !== undefined) && (
        <div className="flex flex-wrap items-center gap-2">
          {/* Left slot: select-all checkbox, counters, etc. */}
          {headerLeft && <div className="flex items-center gap-2 shrink-0">{headerLeft}</div>}

          {/* Filter chips */}
          {filters.length > 0 && (
            <FilterChips
              filters={filters}
              activeValues={filterValues}
              onChange={handleFilterChange}
              onClearAll={handleClearAll}
            />
          )}

          {/* Right-side actions */}
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            {actions}
            {/* Configure columns */}
            {onColumnsChange !== undefined && (
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="chip-filter"
                aria-label="Configure columns"
              >
                <Settings2 className="w-3.5 h-3.5" />
                <span>Columns</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Card: table + footer (single bordered surface) */}
      <div
        className="ds-data-table-card relative w-full overflow-hidden rounded-xl"
        style={{ minHeight: showLoadingOverlay ? 320 : undefined }}
      >
        {showLoadingOverlay && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center"
            style={{
              background: 'color-mix(in srgb, var(--ds-bg-card, var(--vscode-bg-elevated)) 92%, transparent)',
            }}
            role="status"
            aria-live="polite"
            aria-label="Loading"
          >
            <LoadingSpinner size="lg" showText={false} />
          </div>
        )}

        <div className="w-full overflow-x-auto">
          <table
            className="ds-table w-full min-w-[600px]"
            aria-label={ariaLabel}
            role="grid"
            aria-busy={loading}
          >
            <thead>
              <tr>
                {visibleColumns.map((col) => (
                  <Th
                    key={col.key}
                    col={col}
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                ))}
              </tr>
            </thead>

            <tbody>
              {showLoadingOverlay ? (
                <tr aria-hidden="true">
                  <td
                    colSpan={visibleColumns.length}
                    style={{ height: 280, padding: 0, border: 0, lineHeight: 0 }}
                  />
                </tr>
              ) : pagedRows.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length} className="!border-0">
                    <div
                      className="flex flex-col items-center justify-center gap-2 py-14 text-center text-[13px]"
                      style={{ color: 'var(--vscode-text-tertiary)' }}
                    >
                      <span className="text-2xl opacity-40" aria-hidden>∅</span>
                      <p>{emptyMessage}</p>
                    </div>
                  </td>
                </tr>
              ) : (
                pagedRows.map((row, ri) => (
                  <tr
                    key={ri}
                    className={onRowClick ? 'ds-data-table-row--clickable' : undefined}
                    style={{
                      cursor: onRowClick ? 'pointer' : 'default',
                    }}
                    onClick={() => onRowClick?.(row)}
                    onKeyDown={(e) => handleRowKeyDown(e, row)}
                    tabIndex={onRowClick ? 0 : undefined}
                    role={onRowClick ? 'row' : undefined}
                  >
                    {visibleColumns.map((col) => (
                      <td
                        key={col.key}
                        className="max-w-[360px] overflow-hidden text-ellipsis whitespace-nowrap"
                        style={{ minWidth: col.minWidth ?? 120 }}
                      >
                        {col.cell
                          ? col.cell(row[col.key], row)
                          : (row[col.key] == null ? '—' : String(row[col.key]))}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer: centered inside card; optional right reserve for FAB */}
        {!showLoadingOverlay && (
          <div
            className={`ds-data-table-footer flex flex-wrap items-center justify-center gap-x-4 gap-y-2 px-3 py-2.5 text-[12px] ${
              reserveFloatingSpace ? 'pr-[5.5rem] sm:pr-24' : ''
            }`}
            style={{ color: 'var(--vscode-text-secondary)' }}
          >
          <span className="tabular-nums shrink-0">
            {totalCount > 0
              ? `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalCount)} of ${totalCount}`
              : '0 results'}
          </span>

          <span
            className="hidden sm:block h-4 w-px shrink-0"
            style={{ background: 'var(--vscode-border-secondary)' }}
            aria-hidden
          />

          <label className="flex items-center gap-1.5 shrink-0">
            Rows per page:
            <select
              value={pageSize}
              onChange={(e) => changePageSize(Number(e.target.value))}
              className="rounded border px-1.5 py-0.5 text-[12px] bg-transparent focus:outline-none"
              style={{ borderColor: 'var(--vscode-border-primary)', color: 'var(--vscode-text-primary)' }}
            >
              {pageSizeOptions.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>

          <span
            className="hidden sm:block h-4 w-px shrink-0"
            style={{ background: 'var(--vscode-border-secondary)' }}
            aria-hidden
          />

          <div className="flex items-center gap-1 shrink-0" role="navigation" aria-label="Table pagination">
            <NavBtn
              onClick={() => goToPage(Math.max(1, page - 1))}
              disabled={page <= 1}
              label="Previous page"
            >
              ‹
            </NavBtn>
            <span className="min-w-[3.5rem] px-1 text-center tabular-nums">
              {page} / {totalPages}
            </span>
            <NavBtn
              onClick={() => goToPage(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
              label="Next page"
            >
              ›
            </NavBtn>
          </div>
        </div>
        )}
      </div>

      {/* Configure columns drawer */}
      <ConfigureColumnsDrawer
        columns={columns}
        visibility={visibility}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSave={handleSaveColumns}
      />
    </div>
  );
}

export default DataTable;

// ─────────────────────────────────────────────────────────────────────────
// Th (sortable column header)
// ─────────────────────────────────────────────────────────────────────────

interface ThProps<T> {
  col:     Column<T>;
  sortKey: string | null;
  sortDir: SortDir;
  onSort:  (key: string) => void;
}

function Th<T extends Record<string, unknown>>({ col, sortKey, sortDir, onSort }: ThProps<T>) {
  const isSorted  = sortKey === col.key;
  const sortable  = col.sortable !== false;

  return (
    <th
      className="text-left"
      style={{
        minWidth:  col.minWidth ?? 120,
        userSelect:'none',
        whiteSpace:'nowrap',
      }}
    >
      {sortable ? (
        <button
          type="button"
          className="inline-flex items-center gap-1 focus:outline-none focus-visible:underline"
          onClick={() => onSort(col.key)}
          aria-sort={
            isSorted
              ? sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : 'none'
              : 'none'
          }
        >
          {col.header}
          {isSorted && sortDir === 'asc'  && <ArrowUp   className="w-3 h-3" />}
          {isSorted && sortDir === 'desc' && <ArrowDown  className="w-3 h-3" />}
          {(!isSorted || sortDir === null) && <ArrowUpDown className="w-3 h-3 opacity-30" />}
        </button>
      ) : (
        col.header
      )}
    </th>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// NavBtn (pagination prev/next)
// ─────────────────────────────────────────────────────────────────────────

interface NavBtnProps {
  onClick:   () => void;
  disabled:  boolean;
  label:     string;
  children:  React.ReactNode;
}

function NavBtn({ onClick, disabled, label, children }: NavBtnProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="ds-data-table-nav-btn flex h-8 w-8 items-center justify-center rounded-lg text-base leading-none transition-colors disabled:cursor-not-allowed disabled:opacity-30"
    >
      {children}
    </button>
  );
}
